import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useCreateSkillCollection, useListSkillCollections } from "@/queries/skill-collections";

const UNTITLED_SKILL_COLLECTION_PREFIX = "Untitled Skill Collection";
const UNTITLED_SKILL_COLLECTION_NAME_REGEX = /^Untitled Skill Collection #(\d+)$/;

const useCreateDefaultSkillCollection = (folderId: number | null = null, openInNewTab = false) => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const workspaceIdNumber = workspaceId ? Number(workspaceId) : null;
  const { data: skillCollectionsData } = useListSkillCollections(workspaceIdNumber);

  const createMutation = useCreateSkillCollection({
    onSuccess: data => {
      const url = `/workspace/${workspaceId}/skill-collection/${data.skill_collection.id}/version/1/edit`;
      if (openInNewTab) {
        window.open(url, "_blank");
      } else {
        navigate(url);
      }
    },
  });

  const create = async () => {
    if (!workspaceIdNumber) {
      return;
    }

    setIsCreating(true);

    try {
      const nextUntitledNumber =
        skillCollectionsData?.skill_collections.reduce((maxNumber, collection) => {
          const match = collection.name.match(UNTITLED_SKILL_COLLECTION_NAME_REGEX);
          if (!match) {
            return maxNumber;
          }

          return Math.max(maxNumber, Number(match[1]));
        }, 0) ?? 0;

      await createMutation.mutateAsync({
        workspaceId: workspaceIdNumber,
        name: `${UNTITLED_SKILL_COLLECTION_PREFIX} #${nextUntitledNumber + 1}`,
        folderId: folderId ?? undefined,
      });
    } catch {
      // Error handling is already centralized in the mutation hook.
    } finally {
      setIsCreating(false);
    }
  };

  return {
    create,
    isCreating: isCreating || createMutation.isPending,
  };
};

export default useCreateDefaultSkillCollection;
